import { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  TextField,
  Alert,
} from "@mui/material";
import { db } from "../../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { manualToggle, manualReply } from "../../api/webchat";

export default function ConversationDetailDialog({
  open,
  onClose,
  agentId, // ex: "AAylhOlfz0j7L8VEsE3F"
  conversationId, // ex: "web:1760632237884-0btsz1"
}) {
  const [meta, setMeta] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualActive, setManualActive] = useState(false);
  const [manualMessage, setManualMessage] = useState("");
  const [toggling, setToggling] = useState(false);
  const [sendingManual, setSendingManual] = useState(false);
  const scrollRef = useRef(null);
  const endRef = useRef(null);
  const { currentUser } = useAuth();

  // Highlight phones (pink chip) and probable names (blue chip)
  const renderWithHighlights = (text) => {
    if (!text) return "";
    const matches = [];
    // Phones (higher priority)
    const phoneRe = /(\+?\d[\d\s().-]{6,}?\d)/g;
    let m;
    while ((m = phoneRe.exec(text))) {
      const raw = m[1];
      const start = m.index;
      const len = raw.length;
      const normalized = raw.replace(/[^\d+]/g, "");
      const digits = (normalized.match(/\d/g) || []).length;
      if (digits >= 7 && digits <= 15) {
        matches.push({ start, end: start + len, type: "phone", raw, normalized, prio: 2 });
      }
    }
    // Names via explicit cues (FR/EN)
    const cuePatterns = [
      /(je m’appelle|je m'appelle|je suis|mon nom est|prénom\s*:|nom\s*:)[\s:]*([A-Za-zÀ-ÖØ-öø-ÿ'’\-]{2,}(?:\s+[A-Za-zÀ-ÖØ-öø-ÿ'’\-]{2,})?)/giu,
      /(my name is|i am|i’m|i'm|name\s*:|first name\s*:|last name\s*:)[\s:]*([A-Za-zÀ-ÖØ-öø-ÿ'’\-]{2,}(?:\s+[A-Za-zÀ-ÖØ-öø-ÿ'’\-]{2,})?)/giu,
    ];
    for (const re of cuePatterns) {
      let mm;
      while ((mm = re.exec(text))) {
        const full = mm[0];
        const captured = mm[2] || "";
        if (!captured) continue;
        const off = full.indexOf(captured);
        if (off < 0) continue;
        const start = mm.index + off;
        const end = start + captured.length;
        matches.push({ start, end, type: "name", raw: captured, prio: 1 });
      }
    }
    // Names via capitalized pair heuristic
    const capPair = /\b([A-ZÀ-ÖØ-Þ][A-Za-zÀ-ÖØ-öø-ÿ'’\-]{1,24})(?:\s+([A-ZÀ-ÖØ-Þ][A-Za-zÀ-ÖØ-öø-ÿ'’\-]{1,24}))\b/g;
    let mh;
    while ((mh = capPair.exec(text))) {
      const g1 = mh[1];
      const g2 = mh[2];
      const full = `${g1} ${g2}`;
      const start = mh.index;
      const end = start + full.length;
      const stop = new Set(["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday","January","February","March","April","May","June","July","August","September","October","November","December","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche","Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre","Agent","Support","TalkMate"]);
      if (stop.has(g1) || stop.has(g2)) continue;
      matches.push({ start, end, type: "name", raw: full, prio: 0.9 });
    }
    if (matches.length === 0) return text;
    // Resolve overlaps: prefer higher prio
    matches.sort((a, b) => (b.prio - a.prio) || (a.start - b.start));
    const taken = [];
    for (const it of matches) {
      const overlaps = taken.some(t => !(it.end <= t.start || it.start >= t.end));
      if (!overlaps) taken.push(it);
    }
    taken.sort((a, b) => a.start - b.start);
    const out = [];
    let last = 0;
    let idx = 0;
    for (const t of taken) {
      if (t.start > last) out.push(text.slice(last, t.start));
      if (t.type === "phone") {
        const normalized = t.normalized;
        const tel = normalized.startsWith("+") ? normalized : `+${normalized}`;
        out.push(
          <a
            key={`ph-${idx++}-${t.start}`}
            href={`tel:${tel}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "rgba(255,64,129,0.24)",
              border: "1px solid #FF4081",
              borderRadius: 9999,
              padding: "2px 8px",
              margin: "0 2px",
              color: "inherit",
              textDecoration: "none",
              lineHeight: 1.4,
              fontWeight: 700,
            }}
            title={tel}
          >
            {t.raw}
          </a>
        );
      } else {
        out.push(
          <span
            key={`nm-${idx++}-${t.start}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "rgba(33,150,243,0.22)",
              border: "1px solid #2196F3",
              borderRadius: 9999,
              padding: "2px 8px",
              margin: "0 2px",
              fontWeight: 700,
              lineHeight: 1.4,
            }}
            title={t.raw}
          >
            {t.raw}
          </span>
        );
      }
      last = t.end;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };

  // Charge le doc conversation (métadonnées) + écoute les events
  useEffect(() => {
    if (!open || !agentId || !conversationId) return;

    let unsub = null;
    setLoading(true);

    (async () => {
      try {
        // 1) conversation meta
        const convRef = doc(
          db,
          "messages",
          `agent_${agentId}`,
          "conversations",
          conversationId
        );
        const convSnap = await getDoc(convRef);
        setMeta(
          convSnap.exists() ? { id: conversationId, ...convSnap.data() } : null
        );

        // 2) events stream
        const eventsRef = collection(convRef, "events");
        const q = query(eventsRef, orderBy("timestamp", "asc"));
        unsub = onSnapshot(
          q,
          (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setEvents(rows);
            setLoading(false);
          },
          (err) => {
            console.error("[ConvDetail] events onSnapshot ERROR", err);
            setLoading(false);
          }
        );
      } catch (e) {
        console.error("[ConvDetail] load meta ERROR", e);
        setLoading(false);
      }
    })();

    return () => {
      if (unsub) unsub();
    };
  }, [open, agentId, conversationId]);

  useEffect(() => {
    setManualActive(meta?.botActive === false);
  }, [meta?.botActive]);

  useEffect(() => {
    try {
      if (endRef.current) {
        endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      } else if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    } catch {}
  }, [events, open]);

  const fmt = (ts) =>
    ts?.seconds ? new Date(ts.seconds * 1000).toLocaleString() : "";

  const site = meta?.userHints?.site || meta?.userHints?.source || "unknown";
  const resolvedTenantId = meta?.tenantId;

  const toggleManual = async (activateManual) => {
    if (!resolvedTenantId || !agentId || !conversationId) return;
    setToggling(true);
    try {
      await manualToggle({
        tenantId: resolvedTenantId,
        agentId,
        conversationId,
        active: !activateManual, // active=true réactive le bot
        operatorId: currentUser?.uid || null,
      });
      setManualActive(activateManual);
      setMeta((m) => (m ? { ...m, botActive: !activateManual } : m));
    } catch (e) {
      console.error("[ConvDetail] manualToggle failed", e);
    } finally {
      setToggling(false);
    }
  };

  const sendManual = async () => {
    const body = manualMessage.trim();
    if (!body || !resolvedTenantId || !agentId || !conversationId) return;
    setSendingManual(true);
    try {
      await manualReply({
        tenantId: resolvedTenantId,
        agentId,
        conversationId,
        messageBody: body,
      });
      setManualMessage("");
    } catch (e) {
      console.error("[ConvDetail] manualReply failed", e);
    } finally {
      setSendingManual(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ sx: { maxHeight: '80vh' } }}
    >
      <DialogTitle>
        Conversation
        <Typography
          component="span"
          variant="body2"
          sx={{ ml: 1 }}
          color="text.secondary"
        >
          {conversationId}
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ minHeight: 360, maxHeight: "70vh", overflowY: "auto" }}
        ref={scrollRef}
      >
        {/* META */}
        {meta && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
            <Chip size="small" label={`Interactions: ${meta.interactions ?? 0}`} />
            <Chip size="small" label={`Site: ${site}`} />
            {meta?.userHints?.email && (
              <Chip
                size="small"
                label={`Email: ${meta.userHints.email}`}
                color="primary"
                variant="outlined"
              />
            )}
            {meta.updatedAt?.seconds && (
              <Chip size="small" label={`Updated: ${fmt(meta.updatedAt)}`} />
            )}
          </Stack>
        )}

        <Box sx={{ mb: 2 }}>
          <Alert severity={manualActive ? "warning" : "info"} sx={{ alignItems: "center" }}>
            {manualActive
              ? "Bot désactivé pour cette conversation. Vous répondez manuellement."
              : "Bot actif. Vous pouvez passer en réponse manuelle si besoin."}
          </Alert>
        </Box>

        {/* EVENTS */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aucun message pour cette conversation.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {events.map((e) => (
              <Box
                key={e.id}
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  bgcolor:
                    e.from === "user"
                      ? "background.default"
                      : e.from === "bot"
                      ? "action.hover"
                      : "background.paper",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700 }}
                    color={
                      e.from === "user"
                        ? "primary"
                        : e.from === "bot"
                        ? "secondary"
                        : "text.secondary"
                    }
                  >
                    {e.from}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmt(e.timestamp)}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {renderWithHighlights(e.body || "")}
                </Typography>
              </Box>
            ))}
            <div ref={endRef} />
          </Box>
        )}

        {manualActive && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1.5,
              border: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
                }`,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#0f172a" : "#f8fafc",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography variant="subtitle2">Réponse manuelle</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Écrire une réponse manuelle"
                value={manualMessage}
                onChange={(e) => setManualMessage(e.target.value)}
                multiline
                minRows={2}
              />
              <Button
                variant="contained"
                onClick={sendManual}
                disabled={sendingManual || !manualMessage.trim()}
                sx={{ alignSelf: "flex-start", minWidth: 120 }}
              >
                {sendingManual ? "Envoi..." : "Envoyer"}
              </Button>
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            variant={manualActive ? "outlined" : "contained"}
            color={manualActive ? "success" : "warning"}
            onClick={() => toggleManual(!manualActive)}
            disabled={toggling || !resolvedTenantId}
          >
            {manualActive ? "Réactiver le bot" : "Répondre manuellement"}
          </Button>
        </Stack>
        <Button onClick={onClose} variant="outlined">
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
}




